import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  IconButton,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ArchiveIcon from "@mui/icons-material/Archive";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { API_URL } from '../utils/apiConfig';

const SimpleBatchArchiveEmployees = ({ onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [archiveDate, setArchiveDate] = useState(dayjs());
  const [archiveReason, setArchiveReason] = useState("");
  const [step, setStep] = useState(1); // 1: Select Employees, 2: Review & Confirm

  // Debug localStorage
  console.log('localStorage keys:', Object.keys(localStorage));
  console.log('localStorage user:', localStorage.getItem("user"));
  console.log('localStorage token:', localStorage.getItem("token"));
  
  const user = JSON.parse(localStorage.getItem("user"));
  const isAdmin = user?.role === "admin";
  const managerStore = user?.store;
  console.log('Parsed user data:', user); // Debug log

  // Fetch locations
  useEffect(() => {
    const fetchLocations = async () => {
      console.log('=== FETCHING LOCATIONS START ===');
      console.log('API_URL:', API_URL);
      console.log('User role:', user?.role);
      console.log('Is Admin:', isAdmin);
      console.log('Manager Store:', managerStore);
      
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        let url = `${API_URL}/locations`;
        console.log('Fetching from URL:', url);
        console.log('Token exists:', !!token);
        
        // Temporarily disable store filtering to see all locations
        // if (!isAdmin && managerStore) {
        //   url += `?store=${managerStore}`;
        // }
        
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        
        console.log('Response status:', response.status);
        console.log('Response ok:', response.ok);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response:', errorText);
          throw new Error(`Failed to fetch locations: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Fetched locations data:', data);
        console.log('Number of locations:', data?.length || 0);
        console.log('Manager store:', managerStore, 'isAdmin:', isAdmin);
        setLocations(data || []);
        
        // Auto-select location for managers
        if (!isAdmin && managerStore) {
          const managerLocation = data.find((loc) => loc.id == managerStore);
          if (managerLocation) {
            setSelectedLocation(managerLocation.id);
          }
        } else if (data.length === 1) {
          // Auto-select if only one location
          setSelectedLocation(data[0].id);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLocations();
  }, [isAdmin, managerStore]);

  // Fetch employees when location changes
  useEffect(() => {
    if (selectedLocation) {
      fetchEmployees(selectedLocation);
    } else {
      setEmployees([]);
      setFilteredEmployees([]);
    }
  }, [selectedLocation]);

  // Filter employees based on search
  useEffect(() => {
    if (!searchTerm) {
      setFilteredEmployees(employees);
    } else {
      const filtered = employees.filter((emp) => {
        const fullName = `${emp.firstName} ${emp.lastName}`.toLowerCase();
        const email = emp.email?.toLowerCase() || "";
        const position = emp.position?.toLowerCase() || "";
        const search = searchTerm.toLowerCase();
        
        return fullName.includes(search) || 
               email.includes(search) || 
               position.includes(search);
      });
      setFilteredEmployees(filtered);
    }
  }, [searchTerm, employees]);

  const fetchEmployees = async (locationId) => {
    console.log('Fetching employees for location:', locationId);
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/locations/${locationId}/employees`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch employees");
      }
      
      const data = await response.json();
      console.log('Fetched employees:', data);
      
      // Only show active employees
      const activeEmployees = data.filter(emp => 
        emp.current_employement_status !== 'Archived' && 
        emp.currentEmploymentStatus !== 'Archived'
      );
      
      console.log('Active employees:', activeEmployees);
      setEmployees(activeEmployees);
      setFilteredEmployees(activeEmployees);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeToggle = (employeeId) => {
    setSelectedEmployees((prev) => {
      if (prev.includes(employeeId)) {
        return prev.filter((id) => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedEmployees.length === filteredEmployees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(filteredEmployees.map((emp) => emp.id));
    }
  };

  const handleArchive = async () => {
    if (!archiveReason.trim()) {
      setError('Please provide a reason for archiving');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/employees/batch-archive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          employeeIds: selectedEmployees,
          archiveDate: dayjs().format('YYYY-MM-DD'), // Always use today's date
          reason: archiveReason
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to archive employees');
      }

      const data = await response.json();
      console.log('Archive response:', data);
      console.log('Archive results:', data.results);
      
      // Show success message with actual count
      const successCount = data.results?.successful?.length || 0;
      const failedCount = data.results?.failed?.length || 0;
      
      if (successCount > 0) {
        setSuccess(`Successfully archived ${successCount} employee(s). ${failedCount > 0 ? `${failedCount} failed.` : ''}`);
      } else {
        // The server explains every failure per employee ("Already archived",
        // "No permission to archive employees from other locations", "Employee
        // not found"). Telling the user to "check the selection" while throwing
        // that away is what made this look like a broken store rather than a
        // list that should not have offered those people — report what the
        // server actually said.
        const failures = data.results?.failed || [];
        const reasons = [...new Set(failures.map((f) => f.error).filter(Boolean))];
        const who = failures
          .filter((f) => f.name)
          .map((f) => f.name)
          .slice(0, 4)
          .join(', ');
        setError(
          reasons.length
            ? `No employees were archived. ${reasons.join('; ')}${who ? ` (${who}${failures.length > 4 ? `, +${failures.length - 4} more` : ''})` : ''}.`
            : 'No employees were archived. Please check the selection and try again.',
        );
        return;
      }
      
      // Remove successfully archived employees from the list
      const successfulIds = data.results?.successful?.map(emp => emp.employeeId) || [];
      const remainingEmployees = employees.filter(
        emp => !successfulIds.includes(emp.id)
      );
      setEmployees(remainingEmployees);
      setSelectedEmployees([]);
      setArchiveReason('');
      setStep(1);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedEmployeeDetails = employees.filter(emp => 
    selectedEmployees.includes(emp.id)
  );

  if (success) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <ArchiveIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Employees Archived Successfully
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {typeof success === 'string' ? success : `${selectedEmployees.length} employee(s) have been archived`}
        </Typography>
        <Button variant="contained" onClick={onClose} fullWidth>
          Close
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ 
        height: isMobile ? '100%' : 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Header */}
        <Box sx={{ p: 3, pb: 1 }}>
          <Typography variant="h6" component="h2" gutterBottom>
            <ArchiveIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Archive Employees
          </Typography>
          
          {step === 1 && (
            <Typography variant="body2" color="text.secondary">
              Select employees to archive from your location
            </Typography>
          )}
          
          {step === 2 && (
            <Typography variant="body2" color="text.secondary">
              Review and confirm archival details
            </Typography>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'hidden', px: 3 }}>
          {step === 1 && (
            <Stack spacing={2}>
              {/* Location Selection - ALWAYS SHOW FOR DEBUGGING */}
              {console.log('=== RENDER LOCATIONS ===', {
                isAdmin,
                locationsLength: locations.length,
                locations,
                shouldShowDropdown: true // Force to always show
              })}
              {true && ( // Changed to always show
                <FormControl fullWidth>
                  <InputLabel>Location</InputLabel>
                  <Select
                    value={selectedLocation || ''}
                    label="Location"
                    onChange={(e) => setSelectedLocation(e.target.value || null)}
                  >
                    <MenuItem value="">
                      <em>Select Location</em>
                    </MenuItem>
                    {locations.map((location) => (
                      <MenuItem key={location.id} value={location.id}>
                        {location.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Search */}
              {selectedLocation && (
                <TextField
                  fullWidth
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchTerm("")}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              {/* Select All */}
              {filteredEmployees.length > 0 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedEmployees.length === filteredEmployees.length}
                      indeterminate={
                        selectedEmployees.length > 0 &&
                        selectedEmployees.length < filteredEmployees.length
                      }
                      onChange={handleSelectAll}
                    />
                  }
                  label={`Select All (${filteredEmployees.length})`}
                />
              )}

              {/* Employee List */}
              <Box sx={{ 
                flex: 1, 
                overflow: 'auto',
                maxHeight: isMobile ? '40vh' : '50vh',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1
              }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : filteredEmployees.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      {selectedLocation 
                        ? (searchTerm ? "No employees found matching search" : "No active employees")
                        : "Select a location to view employees"}
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ py: 0 }}>
                    {filteredEmployees.map((employee) => (
                      <ListItem
                        key={employee.id}
                        button
                        onClick={() => handleEmployeeToggle(employee.id)}
                        selected={selectedEmployees.includes(employee.id)}
                        divider
                        sx={{ py: 1 }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Checkbox
                            edge="start"
                            checked={selectedEmployees.includes(employee.id)}
                            tabIndex={-1}
                            disableRipple
                            size="small"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={employee.name || `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || employee.email}
                          secondary={
                            <Box>
                              <Typography variant="caption" display="block">
                                {employee.position}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {employee.email}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Stack>
          )}

          {step === 2 && (
            <Stack spacing={3}>
              {/* Warning */}
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Important: Archiving employees will:
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  <li>Permanently disable their login access</li>
                  <li>Encrypt and preserve their data for 7 years</li>
                  <li>Remove them from active employee lists</li>
                  <li>This action cannot be easily reversed</li>
                </Box>
              </Alert>

              {/* Selected Employees */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    <Badge badgeContent={selectedEmployees.length} color="primary" sx={{ mr: 2 }}>
                      Selected Employees
                    </Badge>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {selectedEmployeeDetails.map((emp) => (
                      <ListItem key={emp.id} sx={{ px: 0 }}>
                        <ListItemText
                          primary={emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.email}
                          secondary={emp.email}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>

              {/* Archive Details */}
              <TextField
                fullWidth
                label="Archive Date"
                value={dayjs().format('MMMM D, YYYY')}
                disabled
                helperText="Employees will be archived with today's date"
              />

              <TextField
                fullWidth
                label="Reason for Archiving"
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                multiline
                rows={3}
                required
                placeholder="e.g., End of employment, Position eliminated, etc."
              />
            </Stack>
          )}
        </Box>

        {/* Actions */}
        <Box sx={{ p: 3, pt: 2 }}>
          {step === 1 && (
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={onClose}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => setStep(2)}
                disabled={selectedEmployees.length === 0}
                fullWidth
                startIcon={<ArchiveIcon />}
              >
                Continue ({selectedEmployees.length})
              </Button>
            </Stack>
          )}

          {step === 2 && (
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => setStep(1)}
                fullWidth
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={handleArchive}
                disabled={loading || !archiveReason.trim()}
                fullWidth
                startIcon={loading ? <CircularProgress size={20} /> : <WarningAmberIcon />}
              >
                {loading ? "Archiving..." : "Archive Employees"}
              </Button>
            </Stack>
          )}
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default SimpleBatchArchiveEmployees;